import { customElement } from 'lit/decorators/custom-element.js';
import { container } from 'tsyringe';
import { AbstractStandardTimelineView } from '../abstract-standard-timeline.view';
import type { TimelineViewConfig } from '../_shared/timeline-view.config';
import { IssueTypeRepository } from '../../domain/repositories/issue-type.repository';
import type { Issue } from '../../domain/entities/issue.entity';
import { IssueType } from '../../domain/entities/issue-type.entity';
import type { Entity } from '../../domain/entities/entity';
import type { PopoverListItem } from '@o2d/popover-list';
import { issueTypeRowMenu } from './issue-type-row-menu.helper';
import { html } from 'lit';
import { EntityManager } from '../../persistence/entity.manager';
import { typeRowTitle } from './type-row-title.helper';

const VIEW_CONFIG: TimelineViewConfig = {
    entityType: 'IssueType',
    sort: { strategy: 'property', path: 'description', direction: 'ASC' },
    yItemPath: 'type.uid',
    includes: ['type', 'project', 'employees', 'keywords', 'creator', 'employeeToIssueAssignments'],
    // D16: an abstract root (no issue may name it) gets no row. The value must be the text '0': the
    // backend compares LOWER(column) with the bound value, so `false` or `0` match no row at all (measured).
    queryParams: [{ key: 'isAbstract', comparator: 'eq', value: '0' }]
};

/**
 * Timeline view grouping issues by issue type.
 * Route: /by-type
 *
 * Y-axis: IssueType — overrides populateTemplateFromYAxis to assign issue type
 * to the template issue's type field.
 */
@customElement('o2d-types-view')
export class TypesView extends AbstractStandardTimelineView {
    protected readonly viewStateName = 'types';
    protected readonly viewConfig = VIEW_CONFIG;

    /**
     * Greys out edit and delete where the backend would refuse them (roots, types it cannot delete).
     * canDelete and canMoveRoot are read again first, because they change while the type list stays
     * loaded; the type dialog opened from this menu uses the fresh canMoveRoot to lock its picker.
     * When that read fails, the menu uses what is known and the command guards decide.
     */
    protected override async buildRowContextMenu(entity: Entity): Promise<PopoverListItem[]> {
        if (!(entity instanceof IssueType)) {
            return super.buildRowContextMenu(entity);
        }
        await container.resolve(IssueTypeRepository).refreshPermissions(entity).catch(() => undefined);

        return issueTypeRowMenu(entity);
    }

    /**
     * A root row shows the root's translated name, a child row its root as a small overline (D16, D54),
     * read from the loaded type cache.
     */
    protected override renderRowTitle(entityUid: string, axisTitle: string): unknown {
        const entityManager = container.resolve(EntityManager);
        const { overline, title } = typeRowTitle(entityUid, axisTitle,
            uid => (entityManager.getLoadedEntity(uid) as IssueType | null) ?? null);
        return overline
            ? html`<span class="type-row__root">${overline}</span><span class="type-row__name">${title}</span>`
            : title;
    }

    /** Assigns clicked issue type to template issue. */
    protected override async populateTemplateFromYAxis(template: Issue, yAxisUid: string): Promise<void> {
        const issueTypeRepo = container.resolve(IssueTypeRepository);
        const issueType = await issueTypeRepo.findOneById(yAxisUid);
        if (issueType) {
            template.type = issueType;
        }
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'o2d-types-view': TypesView;
    }
}
